<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import WootSubmitButton from '../../../../components/buttons/FormSubmitButton.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AttachmentUpload from 'dashboard/components/widgets/AttachmentUpload.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

export default {
  name: 'EditCanned',
  components: {
    WootSubmitButton,
    Modal,
    WootMessageEditor,
    AttachmentUpload,
    FluentIcon,
  },
  props: {
    response: {
      type: Object,
      default: () => ({}),
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      id: this.response.id || '',
      shortCode: this.response.short_code || '',
      content: this.response.content || '',
      attachments: [],
      existingAttachmentsList: this.response.attachments || [],
      editCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      // Không bắt buộc content nếu có attachments
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.shortCode}`;
    },
    hasAttachments() {
      return (
        this.attachments.length > 0 || this.existingAttachmentsList.length > 0
      );
    },
    isFormValid() {
      // Cho phép form valid nếu có content hoặc attachments
      const hasContent = this.content && this.content.trim().length > 0;
      const hasAttachments = this.hasAttachments;

      return (
        (hasContent || hasAttachments) &&
        !this.v$.shortCode.$invalid &&
        !this.editCanned.showLoading
      );
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [];
      this.existingAttachmentsList = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;

      // Prepare data for API call
      const updateData = {
        id: this.id,
        short_code: this.shortCode,
        content: this.content,
      };

      // Add attachments if any
      if (this.hasAttachments) {
        updateData.attachments = [
          ...this.existingAttachmentsList,
          ...this.attachments,
        ];
      }

      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', updateData)
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
    onAttachmentAdded(files) {
      // Convert FileList to Array and add to attachments
      const newFiles = Array.from(files);
      this.attachments = [...this.attachments, ...newFiles];
    },
    onAttachmentRemoved(index) {
      this.attachments.splice(index, 1);
    },
    onAttachmentError(error) {
      useAlert(error.message || this.$t('CANNED_MGMT.EDIT.ATTACHMENT_ERROR'));
    },
    // Method to handle existing attachments from API response
    setExistingAttachments(attachments) {
      this.existingAttachmentsList = attachments || [];
    },
    removeExistingAttachment(index) {
      this.existingAttachmentsList.splice(index, 1);
    },
    isImageAttachment(attachment) {
      return attachment.file_type === 'image' && attachment.thumb_url;
    },
    getFileIcon(fileType) {
      if (fileType === 'image') return 'image';
      if (fileType === 'video') return 'video';
      if (fileType === 'audio') return 'audio';
      return 'document';
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="pageTitle"
        :header-content="$t('CANNED_MGMT.EDIT.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
            <span v-if="v$.shortCode.$error" class="message">
              {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Existing Attachments Section -->
        <div v-if="existingAttachmentsList.length > 0" class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.EXISTING_ATTACHMENTS.LABEL') }}
            <div class="existing-attachments">
              <div
                v-for="(attachment, index) in existingAttachmentsList"
                :key="index"
                class="existing-attachment-item"
              >
                <!-- Image Preview -->
                <img
                  v-if="isImageAttachment(attachment)"
                  :src="attachment.thumb_url"
                  :alt="attachment.filename || $t('COMMON.IMAGE')"
                  class="attachment-thumbnail"
                />
                <!-- File Icon -->
                <div v-else class="attachment-file-icon">
                  <FluentIcon :icon="getFileIcon(attachment.file_type)" />
                  <span class="file-name">
                    {{ attachment.filename || $t('COMMON.FILE') }}
                  </span>
                  <span class="file-size">
                    {{ formatFileSize(attachment.file_size) }}
                  </span>
                </div>
                <!-- Remove Button -->
                <button
                  type="button"
                  class="remove-attachment-btn"
                  @click="removeExistingAttachment(index)"
                >
                  <FluentIcon icon="dismiss" />
                </button>
              </div>
            </div>
          </label>
        </div>

        <!-- New Attachment Upload Section -->
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.LABEL') }}
            <AttachmentUpload
              :attachments="attachments"
              :max-files="5"
              :max-size="40 * 1024 * 1024"
              :allowed-types="[
                'image/*',
                'video/*',
                'audio/*',
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              ]"
              @attachment-added="onAttachmentAdded"
              @attachment-removed="onAttachmentRemoved"
              @attachment-error="onAttachmentError"
            />
            <div class="attachment-hint">
              {{
                $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.HINT', {
                  maxFiles: 5,
                  maxSize: 40,
                })
              }}
            </div>
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <WootSubmitButton
            :disabled="!isFormValid"
            :button-text="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :loading="editCanned.showLoading"
          />
          <button class="button clear" @click.prevent="onClose">
            {{ $t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT') }}
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}

// Existing attachments styles
.existing-attachments {
  @apply mb-4;
}

.existing-attachments-title {
  @apply text-sm font-medium text-slate-600 dark:text-slate-400 mb-2;
}

.existing-attachments-list {
  @apply flex flex-wrap gap-2;
}

.existing-attachment-item {
  @apply flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-xs border border-slate-200 dark:border-slate-600;
  @apply max-w-full;
}

.existing-attachment-item.image-preview {
  @apply p-1;
}

.existing-image-thumbnail {
  @apply relative w-12 h-12 rounded overflow-hidden bg-slate-200 dark:bg-slate-600;
  @apply flex-shrink-0;
}

.existing-thumbnail-image {
  @apply w-full h-full object-cover;
}

.existing-image-overlay {
  @apply absolute inset-0 bg-slate-900 bg-opacity-20 flex items-center justify-center;
}

.existing-overlay-icon {
  @apply w-4 h-4 text-white;
}

.existing-file-icon {
  @apply flex items-center justify-center w-8 h-8 bg-slate-200 dark:bg-slate-600 rounded;
  @apply flex-shrink-0;
}

.existing-attachment-preview-icon {
  @apply w-4 h-4 text-slate-500 dark:text-slate-400;
}

.existing-file-info {
  @apply flex flex-col gap-1;
  @apply min-w-0;
  @apply flex-1;
}

.existing-file-name {
  @apply text-slate-700 dark:text-slate-300 truncate max-w-24 font-medium;
}

.existing-file-size {
  @apply text-slate-500 dark:text-slate-400;
}

.existing-remove-button {
  @apply ml-2 text-red-500 hover:text-red-700;
  @apply flex-shrink-0;
  @apply p-1;
}

.existing-remove-icon {
  @apply w-4 h-4;
}
</style>
